import React, { useEffect, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import QuoteListLayer from "../components/QuoteListLayer";
import DashboardFilter from "../components/DashboardFilter";
import { API_URL } from "..";
import { Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 1,
};

const QuoteListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const [dateFilter, setDateFilter] = useState(
    searchParams.get("filter") || "all"
  );

  const [customRange, setCustomRange] = useState({
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
  });

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");

  const [searchColumn, setSearchColumn] = useState(
    searchParams.get("searchColumn") || "all"
  );

  const updateUrlParams = ({
    filter = dateFilter,
    range = customRange,
    page = pagination.page,
    limit = pagination.limit,
    search = searchTerm,
    column = searchColumn,
  }) => {
    const params = new URLSearchParams();

    params.set("filter", filter);
    params.set("page", String(page));
    params.set("limit", String(limit));

    if (search && String(search).trim() !== "") {
      params.set("search", String(search).trim());
      params.set("searchColumn", column || "all");
    }

    if (filter === "custom") {
      if (range.startDate) {
        params.set("startDate", range.startDate);
      }

      if (range.endDate) {
        params.set("endDate", range.endDate);
      }
    }

    navigate(`/quotes?${params.toString()}`, {
      replace: false,
    });
  };

  const fetchQuotes = async ({
    page = 1,
    limit = 25,
    filter = "all",
    range = { startDate: "", endDate: "" },
    search = "",
    column = "all",
  }) => {
    setLoading(true);

    try {
      const user = JSON.parse(localStorage.getItem("user"));

      if (!user) {
        window.location.href = "/sign-in";
        return;
      }

      const params = new URLSearchParams();

      params.set("invoiceStatus", "all");
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("filter", filter);

      if (search && String(search).trim() !== "") {
        params.set("search", String(search).trim());
        params.set("searchColumn", column || "all");
      }

      if (filter === "custom" && range.startDate && range.endDate) {
        params.set("startDate", range.startDate);
        params.set("endDate", range.endDate);
      }

      const response = await fetch(
        `${API_URL}/api/invoice/get/quote?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (response.status === 200) {
        setData(result.invoices || []);
        setPagination(result.pagination || DEFAULT_PAGINATION);
      } else {
        setData([]);
        setPagination(DEFAULT_PAGINATION);
      }
    } catch (error) {
      console.error("Error fetching quotes:", error);
      setData([]);
      setPagination(DEFAULT_PAGINATION);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filterFromUrl = searchParams.get("filter") || "all";
    const startDateFromUrl = searchParams.get("startDate") || "";
    const endDateFromUrl = searchParams.get("endDate") || "";
    const pageFromUrl = Number(searchParams.get("page")) || 1;
    const limitFromUrl = Number(searchParams.get("limit")) || 25;
    const searchFromUrl = searchParams.get("search") || "";
    const searchColumnFromUrl = searchParams.get("searchColumn") || "all";

    const rangeFromUrl = {
      startDate: startDateFromUrl,
      endDate: endDateFromUrl,
    };

    setDateFilter(filterFromUrl);
    setCustomRange(rangeFromUrl);
    setSearchTerm(searchFromUrl);
    setSearchColumn(searchColumnFromUrl);

    fetchQuotes({
      page: pageFromUrl,
      limit: limitFromUrl,
      filter: filterFromUrl,
      range: rangeFromUrl,
      search: searchFromUrl,
      column: searchColumnFromUrl,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const handleFilterChange = (newFilter) => {
    setDateFilter(newFilter);

    if (newFilter !== "custom") {
      updateUrlParams({
        filter: newFilter,
        range: {
          startDate: "",
          endDate: "",
        },
        page: 1,
        limit: pagination.limit,
        search: searchTerm,
        column: searchColumn,
      });
    }
  };

  const handleCustomApply = () => {
    if (!customRange.startDate || !customRange.endDate) return;

    updateUrlParams({
      filter: "custom",
      range: customRange,
      page: 1,
      limit: pagination.limit,
      search: searchTerm,
      column: searchColumn,
    });
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleSearchColumnChange = (value) => {
    setSearchColumn(value);
  };

  const handleSearchSubmit = () => {
    updateUrlParams({
      filter: dateFilter,
      range: customRange,
      page: 1,
      limit: pagination.limit,
      search: searchTerm,
      column: searchColumn,
    });
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    setSearchColumn("all");

    updateUrlParams({
      filter: dateFilter,
      range: customRange,
      page: 1,
      limit: pagination.limit,
      search: "",
      column: "all",
    });
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({
      filter: dateFilter,
      range: customRange,
      page: newPage,
      limit: pagination.limit,
      search: searchTerm,
      column: searchColumn,
    });
  };

  const handleLimitChange = (newLimit) => {
    updateUrlParams({
      filter: dateFilter,
      range: customRange,
      page: 1,
      limit: newLimit,
      search: searchTerm,
      column: searchColumn,
    });
  };

  const refreshCurrentPage = () => {
    fetchQuotes({
      page: pagination.page,
      limit: pagination.limit,
      filter: dateFilter,
      range: customRange,
      search: searchTerm,
      column: searchColumn,
    });
  };

  return (
    <>
      <MasterLayout>
        <Breadcrumb title="Quotes" />

        <div className="mt-4 mb-20">
          <div className="card p-3 border radius-8">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h6 className="mb-1">Quotes</h6>

                <p className="mb-0 text-secondary-light text-sm">
                  Current filter:{" "}
                  <span className="fw-semibold text-primary">
                    {dateFilter === "custom"
                      ? `${customRange.startDate || "-"} to ${
                          customRange.endDate || "-"
                        }`
                      : dateFilter}
                  </span>
                </p>
              </div>

              <div className="text-end">
                <span className="badge bg-primary">
                  {pagination.total || 0} Quotes
                </span>
              </div>
            </div>

            <div className="mt-3">
              <DashboardFilter
                filter={dateFilter}
                onFilterChange={handleFilterChange}
                customRange={customRange}
                setCustomRange={setCustomRange}
                onCustomApply={handleCustomApply}
                loading={loading}
              />
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center mt-4">
            <Spinner />
          </div>
        )}

        {!loading && (
          <QuoteListLayer
            jobsData={data}
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            loading={loading}
            searchTerm={searchTerm}
            searchColumn={searchColumn}
            onSearchColumnChange={handleSearchColumnChange}
            onSearchTermChange={handleSearchChange}
            onSearchSubmit={handleSearchSubmit}
            onSearchClear={handleSearchClear}
            onRefresh={refreshCurrentPage}
          />
        )}
      </MasterLayout>
    </>
  );
};

export default QuoteListPage;